/**
 * nonError — ce qu'on peut dire d'une valeur rejetée ou levée qui n'est pas une `Error`, et le
 * message d'une cause tel qu'il peut partir.
 *
 * Sur Android, react-native-webrtc rejette une caméra ou un micro qui ne s'ouvre pas avec un
 * `MediaStreamError` (`{ name: "DOMException", message: "AbortError" }`), et livekit-client a rejeté
 * l'`Event` d'erreur d'un WebSocket : ni l'un ni l'autre n'est une `Error`.
 *
 * `kind` les distingue et sert de groupe : le nom de l'objet, le type d'un événement, ou le type
 * JavaScript. Ce qui varie d'un cas à l'autre (un message, un texte de serveur) reste dans `message`,
 * jamais dans `kind`, pour qu'un message changeant ne découpe pas le groupe.
 *
 * Le nom d'un objet ne devient `kind` que s'il se lit comme une classe d'erreur (`DOMException`,
 * `NotAllowedError`) : `kind` part tel quel dans le groupe Bugsnag et la télémétrie, et le `name`
 * d'un autre objet rejeté (une fiche d'élève) peut être celui d'une personne.
 */
export type NonErrorDescription = {
    kind: string
    message: string | null
}

/** Au-delà, le message d'une cause est coupé : il dit ce qui a cassé, pas tout ce qu'un serveur a écrit. */
export const CAUSE_MESSAGE_MAX = 300

const ERROR_CLASS_NAME = /^[A-Z][A-Za-z]*(?:Error|Exception)$/

const JWT = /eyJ[\w-]+\.[\w-]+\.[\w-]+/g
const TOKEN_PARAM = /((?:access_)?token=)[^&\s"']+/gi

/** Le message d'une cause, sans jeton (un JWT, un `access_token=` d'une adresse), et borné. */
export function causeMessageOf(message: string): string {
    const redacted = message.replace(TOKEN_PARAM, "$1<redacted>").replace(JWT, "<redacted>")
    return redacted.length > CAUSE_MESSAGE_MAX ? `${redacted.slice(0, CAUSE_MESSAGE_MAX - 1)}…` : redacted
}

export function describeNonError(value: unknown): NonErrorDescription {
    if (value == null) return { kind: String(value), message: null }
    if (typeof value === "string") return { kind: "string", message: value }
    if (typeof value !== "object") return { kind: typeof value, message: String(value) }

    const candidate = value as {
        name?: unknown
        message?: unknown
        type?: unknown
        _type?: unknown
        timeStamp?: unknown
        _timeStamp?: unknown
    }
    if (typeof candidate.name === "string" && ERROR_CLASS_NAME.test(candidate.name)) {
        const message = typeof candidate.message === "string" && candidate.message !== "" ? candidate.message : null
        return { kind: candidate.name, message }
    }

    // Un événement a un type et un horodatage : l'`Event` de React Native les garde dans `_type` et
    // `_timeStamp`, celui d'event-target-shim dans `type` et `timeStamp`. L'horodatage écarte une
    // action Redux ou tout objet qui n'aurait qu'un `type`.
    const eventType = typeof candidate.type === "string" ? candidate.type : candidate._type
    const stamped = typeof candidate.timeStamp === "number" || typeof candidate._timeStamp === "number"
    if (typeof eventType === "string" && stamped) return { kind: `Event ${eventType}`, message: null }

    return { kind: "object", message: null }
}

/** La description en une ligne, son message sans jeton et borné : `DOMException: AbortError`, `Event error`. */
export function nonErrorText({ kind, message }: NonErrorDescription): string {
    return message == null ? kind : `${kind}: ${causeMessageOf(message)}`
}
